/**
 * Which SPA and CLI releases call the cloud API (D37 §4.1, §4.8; `GET /vendor/clients`): per release its build time,
 * requests, how many were refused below the SPA floor (`426 upgrade-required`) and its calls to deprecated routes —
 * the metric a route's removal waits on (contracts/deprecations.md). `none` is traffic without `x-acp-client`: every
 * client from before it. The counts are approximate (each API instance writes them once a minute).
 */
import type { VendorClientsResponseWire } from '@acp/api-contract/vendor.js';
import { RefreshCwIcon } from 'lucide-react';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type ClientReleasesPanelProps = {
  readonly clients: VendorClientsResponseWire | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
};

function day(iso: string | null): string {
  return iso === null ? '–' : iso.slice(0, 10);
}

export function ClientReleasesPanel({ clients, loading, error, onRetry }: ClientReleasesPanelProps) {
  return (
    <Card data-testid="client-releases">
      <CardHeader>
        <CardTitle>Client releases</CardTitle>
        <CardDescription data-testid="client-floor">
          {clients === null
            ? 'The SPA and CLI builds calling the API.'
            : `Last ${String(clients.days)} days. SPA floor: ${clients.webFloor === null ? 'none' : `builds before ${day(clients.webFloor)} are asked to reload`}.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {error === null ? null : (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-3 text-sm">
            <span className="text-destructive">Could not load the client releases: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        )}
        {clients === null && loading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
        {clients !== null && clients.clients.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="clients-none">
            No calls recorded.
          </p>
        ) : null}
        {clients === null || clients.clients.length === 0 ? null : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Built</TableHead>
                <TableHead className="text-right">Requests</TableHead>
                <TableHead className="text-right">Refused</TableHead>
                <TableHead>Deprecated calls</TableHead>
                <TableHead>Last seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.clients.map((c) => (
                <TableRow key={c.release} data-testid="client-row" data-release={c.release}>
                  <TableCell>
                    <span className="font-mono text-xs">{c.release}</span>{' '}
                    {c.kind === 'none' ? (
                      <Badge variant="outline" title="Requests without x-acp-client: every SPA and CLI from before D37 wave 3">
                        unidentified
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm">{day(c.builtAt)}</TableCell>
                  <TableCell className="text-right text-sm">{c.requests}</TableCell>
                  <TableCell className="text-right">
                    {c.refused === 0 ? (
                      <span className="text-sm text-muted-foreground">0</span>
                    ) : (
                      <Badge variant="warning" data-testid="client-refused">
                        {c.refused}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {c.deprecatedCalls.length === 0 ? (
                      <span className="text-muted-foreground">–</span>
                    ) : (
                      <span data-testid="client-deprecated">{c.deprecatedCalls.map((d) => `${d.routeId} ×${String(d.calls)}`).join(', ')}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.lastSeenAt.slice(0, 16).replace('T', ' ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
